import React, { useEffect, useMemo, useState } from "react";
import Dropdown from "./dropdown";
import TextInput from "./text-input";
import Button from "./button";
import { ICON_ID } from "../icons/google-icon";
import styled from "styled-components";
import { SelectChangeEvent } from "@mui/material/Select";
import CurrencyInput from "./currency-input";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { showError, showSuccess } from "../notification/toastr-actions";
interface EditableFieldProps {
  type: "text" | "dropdown" | "currency" | "number";
  value: string | number;
  error?: string | null;
  options?: { value: string; label: string }[];
  label: string;
  displayValue?: string;
  onSave: (value: string | number) => void;
  onLoad?: () => void;

}

const EditableField: React.FC<EditableFieldProps> = ({
  type,
  value,
  error,
  options,
  label,
  displayValue,
  onSave,
  onLoad
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(displayValue ?? value);
  const [saving, setSaving] = useState(false);
  const dispatch = useAppDispatch();

  const handleSave = () => {
    try {
      setSaving(true);
      onSave(localValue); // parent can call dispatch/applyNewValues
      setIsEditing(false); // only exit edit mode if successful
      dispatch(showSuccess(label + " updated"))
    } catch (error) {
      dispatch(showError(label + " update failed"))
      console.error("Save failed", error);
    } finally {
      setSaving(false);
    }
  };


  const handleCancel = () => {
    setLocalValue(value);  // reset value
    setIsEditing(false);   // exit edit mode
  };


  const idRef = React.useRef(crypto.randomUUID());
  const id = idRef.current;

  return (
    <>
      {isEditing ? (
        <FieldContainer>
          {type === "text" || type === "number" ? (
            <TextInput
              label={label}
              type={type}
              id={id}
              value={localValue as string}
              error={error}
              multiline={type == "text"}
              onChange={(event) => {
                setLocalValue(event.target.value as string)
              }}
              sx={{ marginTop: "0px", marginBottom: "0px" }}
            />
          ) : type === "dropdown" ? (
            <Dropdown
              label={label}
              id={id}
              value={localValue as string}
              options={options || []}
              variant="outlined"
              onChange={(e: SelectChangeEvent<unknown>) =>
                setLocalValue(e.target.value as string)
              }
              autoWidth={false}
              multiple={false}
              native={false}
            />
          ) : type === "currency" ? (
            <CurrencyInput
              label={label}
              value={localValue}
              onChange={(event) => {
                const newValue = parseFloat(event.target.value);
                setLocalValue(newValue);
              }}
            />
          ) : null}
          <VerticalIcons>
            <Button
              variant="text"
              id={id}
              label=""
              icon={ICON_ID.Save}
              isPrimary={true}
              fullWidth={false}
              onClick={handleSave}
              disabled={saving}
            />
            <Button
              variant="text"
              id={id}
              label=""
              icon={ICON_ID.Close}
              isPrimary={true}
              fullWidth={false}
              disabled={saving}
              onClick={handleCancel}
            />
          </VerticalIcons>
        </FieldContainer>
      ) : (
        <EditButtonContainer>
          <TextContainer>{displayValue ?? value}</TextContainer>
          <EditButton
            variant="text"
            id={id}
            label=""
            icon={ICON_ID.Edit}
            isPrimary={true}
            fullWidth={false}
            onClick={() => {
              if (onLoad) {
                onLoad()
              }
              setIsEditing(true)
            }}
          />
        </EditButtonContainer>
      )}
    </>
  );
};

export default React.memo(EditableField);
const VerticalIcons = styled.div`
  display: flex;
  flex-direction: column;

  button {
    span {
      font-size: 0.9rem;
    }
  }
`;
const FieldContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
`;

const EditButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
const EditButton = styled(Button)`
  flex-shrink: 0;
`;
const TextContainer = styled.span`
  flex-grow: 1;
  white-space: break-spaces;
`;


